import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, FileText, Loader2, Printer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import BrandTrustStrip from '../components/BrandTrustStrip';

type PublicPayload = {
  ok: boolean;
  error?: string;
  proposta?: Record<string, unknown>;
  itens?: unknown[];
  validade_ate?: string;
};

export default function Fechamento() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [data, setData] = useState<PublicPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [tel, setTel] = useState('');
  const [forma, setForma] = useState<'pix' | 'infinitepay' | 'na_entrega'>('pix');
  const [terms] = useState('v1');
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const validToken = useMemo(() => (token ? token : null), [token]);

  useEffect(() => {
    if (!validToken) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data: raw, error } = await supabase.rpc('get_proposta_publica', {
        p_token: validToken,
      });
      if (error) {
        setData({ ok: false, error: error.message });
      } else {
        setData(raw as unknown as PublicPayload);
      }
      setLoading(false);
    })();
  }, [validToken]);

  async function handleAceitar(e: React.FormEvent) {
    e.preventDefault();
    if (!validToken) return;
    setSubmitting(true);
    const { data: raw, error } = await supabase.rpc('aceitar_proposta_publica', {
      p_token: validToken,
      p_cliente_nome: nome,
      p_cliente_email: email,
      p_cliente_telefone: tel,
      p_forma_pagamento: forma,
      p_terms_version: terms,
    });
    if (error) {
      setSubmitting(false);
      alert(error.message);
      return;
    }
    const j = raw as { ok?: boolean; proposta_id?: string };
    if (!j?.ok) {
      setSubmitting(false);
      alert(JSON.stringify(raw));
      return;
    }

    if (forma === 'infinitepay') {
      const { data: checkout, error: ckErr } = await supabase.functions.invoke('infinitepay-public-checkout', {
        body: { public_token: validToken },
      });
      setSubmitting(false);
      if (ckErr) {
        alert(ckErr.message || 'Falha ao gerar link de pagamento.');
        setAccepted(true);
        return;
      }
      const payload = checkout as { checkoutUrl?: string; stub?: boolean; message?: string };
      if (payload?.checkoutUrl) {
        window.location.href = payload.checkoutUrl;
        return;
      }
      alert(payload?.message || 'Link InfinitePay indisponível. A equipe enviará o pagamento.');
    }

    setSubmitting(false);
    setAccepted(true);
  }

  function handlePrint() {
    window.print();
  }

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto flex max-w-lg flex-1 flex-col justify-center px-4 py-20 text-center">
          <FileText className="mx-auto h-12 w-12 text-lunins-muted" />
          <h1 className="mt-6 font-display text-2xl font-bold text-lunins-ink">Link incompleto</h1>
          <p className="mt-2 text-lunins-muted">
            Abra o link completo que a equipe Lunins enviou (por e-mail ou WhatsApp). Se você digitou o endereço à mão,
            pode ter faltado copiar a parte final do convite — peça o link de novo à equipe.
          </p>
          <Link to="/" className="mt-8 text-lunins-accent hover:underline">
            Ir ao início
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <Loader2 className="h-10 w-10 animate-spin text-lunins-accent" />
          <p className="text-lunins-muted">Carregando sua proposta…</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!data?.ok) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto flex max-w-lg flex-1 flex-col justify-center px-4 py-20 text-center">
          <p role="alert" className="text-red-300">
            Não foi possível abrir esta proposta. Confira se o link está completo ou peça um novo convite à equipe Lunins.
          </p>
          <Link to="/" className="mt-8 text-lunins-accent hover:underline">
            Ir ao início
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const prop = data.proposta as Record<string, unknown>;
  const itens = (data.itens as Record<string, unknown>[]) || [];
  const totalCent = Number(prop.valor_total_centavos) || 0;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1 bg-hero-mesh">
        <div className="mx-auto max-w-3xl px-4 py-10 md:py-14">
          <article
            id="proposta-print"
            className="overflow-hidden rounded-2xl border border-lunins-accent/20 bg-lunins-surface/80 shadow-card backdrop-blur-sm"
          >
            <div className="border-b border-lunins-border bg-gradient-to-r from-lunins-accent/10 to-transparent px-6 py-5 md:px-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-lunins-accent">Proposta comercial</p>
              <h1 className="mt-1 font-display text-2xl font-bold text-lunins-ink md:text-3xl">Lunins</h1>
              {Boolean(prop.vendedor_nome && String(prop.vendedor_nome).trim()) && (
                <p className="mt-2 text-sm text-lunins-muted">
                  Consultor Lunins: <strong className="text-lunins-ink">{String(prop.vendedor_nome)}</strong>
                </p>
              )}
            </div>
            <div className="space-y-6 px-6 py-6 md:px-8 md:py-8">
              <BrandTrustStrip />
              {Boolean(prop.instituicao_contexto && String(prop.instituicao_contexto).trim()) && (
                <p className="text-lunins-ink/80">
                  <span className="font-semibold text-lunins-ink">Contexto: </span>
                  {String(prop.instituicao_contexto)}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="rounded-lg bg-lunins-sand px-3 py-1.5 text-lunins-ink/80">
                  Desconto: <strong className="text-lunins-accent">{String(prop.desconto_percentual)}%</strong>
                </span>
                <span className="rounded-lg bg-lunins-sand px-3 py-1.5 text-lunins-ink/80">
                  Validade:{' '}
                  <strong className="text-lunins-ink">
                    {prop.validade_ate ? new Date(String(prop.validade_ate)).toLocaleString('pt-BR') : '—'}
                  </strong>
                </span>
              </div>

              <ul className="divide-y divide-white/[0.06] rounded-xl border border-lunins-border">
                {itens.map((it) => (
                  <li key={String(it.id)} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
                    <span className="text-lunins-ink/80">
                      {String(it.produto_nome)} <span className="text-lunins-muted">× {String(it.quantidade)}</span>
                    </span>
                    <span className="font-semibold text-lunins-ink">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        Number(it.subtotal_centavos) / 100
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex items-baseline justify-between border-t border-lunins-border pt-4">
                <span className="font-display text-lg text-lunins-muted">Total</span>
                <span className="font-display text-2xl font-bold text-lunins-ink md:text-3xl">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalCent / 100)}
                </span>
              </div>

              <p className="rounded-lg bg-lunins-surface p-4 text-xs leading-relaxed text-lunins-muted">
                <strong className="text-lunins-muted">Prazo de entrega</strong> definido com a equipe Lunins após confirmação
                do pagamento, conforme região e esta proposta. <strong className="text-lunins-muted">Frete grátis</strong>{' '}
                vinculado ao desconto da campanha{' '}
                <strong className="text-lunins-muted">somente para entrega na mesma cidade</strong> acordada; demais regiões
                conforme combinado.
              </p>
            </div>
          </article>

          {!accepted ? (
            <form
              className="no-print mt-8 rounded-2xl border border-lunins-border bg-lunins-elevated/90 p-6 shadow-card md:p-8"
              onSubmit={handleAceitar}
            >
              <h2 className="font-display text-lg font-bold text-lunins-ink">Confirmar aceite</h2>
              <p className="mt-1 text-sm text-lunins-muted">Preencha seus dados para registrar a intenção conforme esta proposta.</p>
              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-lunins-ink/80">Nome completo</span>
                  <input
                    required
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                    placeholder="Seu nome"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-lunins-ink/80">E-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                    placeholder="opcional"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-lunins-ink/80">WhatsApp (com DDD)</span>
                  <input
                    required
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                    placeholder="67 99999-9999"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-lunins-ink/80">Forma de pagamento preferida</span>
                  <select
                    value={forma}
                    onChange={(e) => setForma(e.target.value as 'pix' | 'infinitepay' | 'na_entrega')}
                    className="mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                  >
                    <option value="pix">PIX</option>
                    <option value="infinitepay">Cartão (link InfinitePay)</option>
                    <option value="na_entrega">Pagar na entrega</option>
                  </select>
                </label>
              </div>
              <p className="mt-4 text-xs text-lunins-muted">
                Ao confirmar, você declara ciência das condições comerciais e da política de privacidade aplicáveis.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-lunins-ink py-4 text-base font-semibold text-lunins-cream shadow-glow transition hover:bg-lunins-ink-soft disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {submitting ? 'Enviando…' : 'Confirmar aceite'}
              </button>
            </form>
          ) : (
            <div className="no-print mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 md:p-8">
              <div className="flex gap-3">
                <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-400" />
                <div>
                  <p className="font-display text-lg font-bold text-lunins-ink">Aceite registrado</p>
                  <p className="mt-2 text-sm text-lunins-muted">
                    {forma === 'infinitepay'
                      ? 'Se o pagamento por cartão não abriu automaticamente, a equipe Lunins enviará o link InfinitePay.'
                      : 'A equipe Lunins enviará as instruções de pagamento ou o link (InfinitePay), conforme a forma escolhida.'}
                  </p>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-lunins-ink/15 bg-white/10 px-5 py-2.5 text-sm font-semibold text-lunins-ink transition hover:bg-white/15"
                  >
                    <Printer className="h-4 w-4" />
                    Imprimir / salvar PDF
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
